import type { AwarenessState, User } from "@/components/Editor/types";
import { Loading } from "@/components/Loading";
import { FileSystem } from "@/utils/yjs/FileSystem";
import {
	DEFAULT_TEMPLATES,
	TemplateType,
	type Template,
} from "@/utils/yjs/constants";
import { base64ToArrayBuffer } from "@/utils/yjs/helpers/base64";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { createId } from "@paralleldrive/cuid2";
import { usePostHog } from "posthog-js/react";
import React, { type ReactNode, useEffect, useState } from "react";
import { IndexeddbPersistence } from "y-indexeddb";
import type { Awareness } from "y-protocols/awareness";
import * as Y from "yjs";
import { create } from "zustand";
import { getRandomUserInfo } from "./helpers/randomUserInfo";

export interface EditorStateStore {
	ydoc: Y.Doc;
	syncProvider?: HocuspocusProvider;
	persistenceProvider?: IndexeddbPersistence;
	awareness?: Awareness;
	yfs?: FileSystem;
	template: Template;
	applyTemplate: (template: Template) => void;
	awarenessUsers: Record<number, User>;
	shareCode?: string;
	/** Sets a shareCode and initializes a sync provider */
	setShareCode: (shareCode?: string) => void;
	projectId?: string;
	/** Sets a projectID and initializes a persistence provider */
	setProjectId: (projectId: string) => void;
}

export const useEditorStateStore = create<EditorStateStore>()((set, get) => ({
	ydoc: new Y.Doc(),
	syncProvider: undefined,
	awareness: undefined,
	template: DEFAULT_TEMPLATES.javascript as Template,
	applyTemplate: (template) => {
		// Init YFS
		const yfs = get().yfs ?? new FileSystem(get().ydoc);
		set(() => ({ yfs }));

		if (template && yfs.readdir("/").length === 0) {
			Y.applyUpdate(get().ydoc, base64ToArrayBuffer(template.value));
			for (const key in get().ydoc.getMap("fs").keys()) {
				console.log("key", key);
			}
		}

		// Set the template in the state
		set(() => ({ template }));
	},
	awarenessUsers: {},
	shareCode: undefined,
	projectId: undefined,
	setProjectId: (projectId) => {
		// Init YFS
		const yfs = get().yfs ?? new FileSystem(get().ydoc);
		set(() => ({ yfs }));

		const { ydoc, persistenceProvider } = get();
		if (ydoc) {
			// Destroy any existing persistence provider
			persistenceProvider?.destroy();

			// Init a new in
			set(() => ({
				persistenceProvider: new IndexeddbPersistence(projectId, ydoc),
			}));
		}
		set(() => ({ projectId }));
	},
	setShareCode: (shareCode) => {
		// Init YFS
		const yfs = get().yfs ?? new FileSystem(get().ydoc);
		set(() => ({ yfs }));

		const { ydoc, syncProvider: oldSyncProvider } = get();

		// Destroy any existing sync provider
		if (oldSyncProvider) {
			oldSyncProvider.destroy();
		}

		if (shareCode) {
			console.log("initing sync provider with share code", shareCode);
			const syncProvider = new HocuspocusProvider({
				url:
					process.env.NODE_ENV === "development"
						? "ws://localhost:4444"
						: "wss://hocuspocus-aged-wildflower-5447.fly.dev/",
				name: shareCode,
				document: ydoc,
			});
			const awareness = syncProvider.awareness as Awareness;
			set(() => ({ awareness, syncProvider }));

			function setUsers() {
				set(() => ({
					awarenessUsers: Object.fromEntries(
						[...(awareness.getStates() as Map<number, AwarenessState>)].map(
							([id, state]) => [id, state.user],
						),
					),
				}));
			}
			awareness.on("change", setUsers);
			// Initialize local user
			let me: User;
			if (window.localStorage.getItem("anonMeUser")) {
				// biome-ignore lint/style/noNonNullAssertion: Checked above
				me = JSON.parse(window.localStorage.getItem("anonMeUser")!);
			} else {
				me = getRandomUserInfo();
				window.localStorage.setItem("anonMeUser", JSON.stringify(me));
			}
			awareness.setLocalStateField("user", me);
		}

		set(() => ({ shareCode }));
	},
}));
